// 📈 B242 MVP2 (AI_112) — DADOS DE GRÁFICO (determinístico, puro, testável)
// ─────────────────────────────────────────────────────────────────────────────
// REGRA DE OURO: o CÓDIGO calcula os números do gráfico (a IA não entra aqui).
// Reusa a estatística descritiva (resumirNumerico/resumirCategorico) e divide a
// coorte nas MESMAS colunas da Table 1 (Total + estratos). A privacidade (n<5) é
// sinalizada por `pequeno`; a UI mascara antes de desenhar.
import { RegistroPesquisa, ValorRegistro } from "../types";
import {
  resumirNumerico,
  resumirCategorico,
  N_MINIMO_CELULA,
} from "./estatisticasDescritivas";

function ehFaltante(v: ValorRegistro): boolean {
  return v === null || v === undefined || v === "";
}

// Divide a coorte em colunas (Total + um estrato por valor) — ESPELHA montarTabela1.
export function dividirColunas(
  registros: RegistroPesquisa[],
  estratificarPor?: string
): { coluna: string; registros: RegistroPesquisa[] }[] {
  const colunas: { coluna: string; registros: RegistroPesquisa[] }[] = [
    { coluna: "Total", registros },
  ];
  if (estratificarPor) {
    const grupos = new Map<string, RegistroPesquisa[]>();
    for (const r of registros) {
      const v = r[estratificarPor];
      if (ehFaltante(v)) continue;
      const k = typeof v === "boolean" ? (v ? "Sim" : "Não") : String(v).trim();
      if (!grupos.has(k)) grupos.set(k, []);
      grupos.get(k)!.push(r);
    }
    Array.from(grupos.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .forEach(([k, regs]) => colunas.push({ coluna: k, registros: regs }));
  }
  return colunas;
}

// ── Box plot (variável numérica) ──────────────────────────────────────────────
export interface BoxplotColuna {
  coluna: string;
  n: number;
  min: number | null;
  q1: number | null;
  mediana: number | null;
  q3: number | null;
  max: number | null;
  pequeno: boolean; // n<5 → a UI mascara (não desenha)
}

export function dadosBoxplot(
  registros: RegistroPesquisa[],
  chave: string,
  estratificarPor?: string
): BoxplotColuna[] {
  return dividirColunas(registros, estratificarPor).map(({ coluna, registros: regs }) => {
    const s = resumirNumerico(regs.map((r) => r[chave]));
    return {
      coluna,
      n: s.n,
      min: s.min,
      q1: s.q1,
      mediana: s.mediana,
      q3: s.q3,
      max: s.max,
      pequeno: s.n > 0 && s.n < N_MINIMO_CELULA,
    };
  });
}

// Amplitude global (para um eixo X compartilhado entre as colunas não-mascaradas).
export function dominioBoxplot(colunas: BoxplotColuna[]): { min: number; max: number } | null {
  const mins: number[] = [];
  const maxs: number[] = [];
  for (const c of colunas) {
    if (c.pequeno || c.n === 0) continue;
    if (c.min !== null) mins.push(c.min);
    if (c.max !== null) maxs.push(c.max);
  }
  if (mins.length === 0 || maxs.length === 0) return null;
  const min = Math.min(...mins);
  const max = Math.max(...maxs);
  return { min, max: max === min ? min + 1 : max }; // evita divisão por zero
}

// ── Barras (variável categórica/booleana) ─────────────────────────────────────
export interface BarraCategoria {
  categoria: string;
  n: number;
  proporcao: number; // 0..1 (sobre quem TEM o dado naquela coluna)
  pequeno: boolean;
}
export interface BarrasColuna {
  coluna: string;
  total: number; // n com dado na coluna
  categorias: BarraCategoria[];
}

export function dadosBarras(
  registros: RegistroPesquisa[],
  chave: string,
  estratificarPor?: string
): BarrasColuna[] {
  return dividirColunas(registros, estratificarPor).map(({ coluna, registros: regs }) => {
    const s = resumirCategorico(regs.map((r) => r[chave]));
    return {
      coluna,
      total: s.n,
      categorias: s.categorias.map((c) => ({
        categoria: c.valor,
        n: c.n,
        proporcao: c.proporcao,
        pequeno: c.pequeno,
      })),
    };
  });
}

// Rótulos de categoria distintos (união entre colunas), preservando a ordem da
// 1ª coluna — para alinhar séries no gráfico agrupado.
export function rotulosCategorias(colunas: BarrasColuna[]): string[] {
  const vistos = new Set<string>();
  const ordem: string[] = [];
  for (const col of colunas) {
    for (const cat of col.categorias) {
      if (!vistos.has(cat.categoria)) {
        vistos.add(cat.categoria);
        ordem.push(cat.categoria);
      }
    }
  }
  return ordem;
}
